'use client';

import { ReactElement, useCallback } from 'react';
import { useStoreMap } from 'effector-react';
import { Button, ButtonType } from '@nx-frontend/shared/ui/button';
import { getRecentFileIdsParams } from '@nx-frontend/shared/store/feature-files';
import { PortalStyleConfig, ThemeColorSchemas } from '@nx-frontend/shared/types';
import { FileListItem, FileListItemColumn } from '../file-list-item';
import { useStartFilesUpload } from '../hooks/useStartFilesUpload';
import { UploadContext } from '../model';

interface FileListResentProps {
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  numberOfItems: number;
  userId: number;
  orgRole: string;
  context: UploadContext;
  fileOpenHost: string;
  allowedTargets?: string[];
  viewAllButton?: ReactElement;
  targetPageIds?: string[];
  personalizationUserId?: number | null;
}

export const FilesRecent: React.FC<FileListResentProps> = ({
  themeColors,
  themeStyle,
  numberOfItems,
  userId,
  viewAllButton,
  context,
  fileOpenHost,
  allowedTargets,
  orgRole,
  targetPageIds,
  personalizationUserId,
}) => {
  const fileIds = useStoreMap(
    getRecentFileIdsParams(numberOfItems, allowedTargets, targetPageIds, personalizationUserId)
  );
  const isEmpty = !fileIds.length;
  const startFilesUpload = useStartFilesUpload();
  const handleUploadFiles = useCallback(() => {
    startFilesUpload(context);
  }, [context, startFilesUpload]);

  const showUploadButton = orgRole !== 'visitor' && context;

  return (
    <>
      <div className="py-2 flex-1 overflow-x-auto max-h-[342px]">
        {!isEmpty &&
          fileIds.map((id, index, array) => (
            <FileListItem
              key={id}
              id={id}
              themeColors={themeColors}
              themeStyle={themeStyle}
              userId={userId}
              fileOpenHost={fileOpenHost}
              visibleColumns={[FileListItemColumn.NAME, FileListItemColumn.CREATED]}
              columnWidths={{
                name: '95%',
                created: '120px',
              }}
              hasBorder={!(index === array.length - 1)}
              canCreateEsign={false}
              canSubmitEsign={false}
              personalizationUserId={personalizationUserId}
            />
          ))}
        {isEmpty && <p className="text-base text-gray-600">There are no files yet</p>}
      </div>
      <div className="flex">
        {viewAllButton}
        {showUploadButton && (
          <Button
            type={ButtonType.BASIC}
            className="!px-0 ml-6"
            onClick={handleUploadFiles}
            themeStyle={themeStyle}
            themeColors={themeColors}
          >
            Upload files
          </Button>
        )}
      </div>
    </>
  );
};
